import { useRef, useState } from "react";
import type { ReactElement } from "react";
import { Alert, Form, Input, Modal } from "antd";
import { auditGoods, changeGoodsShelf } from "@/api/shopGoods";
import type { GoodsRow } from "@/types/shopGoods";
import { canChangeGoodsShelf } from "./goodsForm";

export interface GoodsAction {
  type: "approve" | "reject" | "shelf";
  row: GoodsRow;
}
interface GoodsActionDialogProps {
  action: GoodsAction;
  onSaved: () => void;
  onCancel: () => void;
}
interface AuditValues { auditRemark?: string }

export function GoodsActionDialog({ action, onSaved, onCancel }: GoodsActionDialogProps): ReactElement {
  const [form] = Form.useForm<AuditValues>();
  const [busy, setBusy] = useState(false);
  const submitting = useRef(false);
  const { type, row } = action;
  const isShelf = row.isShelf === 1 ? 0 : 1;
  const title = type === "approve" ? "通过商品审核" : type === "reject" ? "驳回商品审核" : isShelf === 1 ? "上架商品" : "下架商品";
  const allowed = type === "shelf"
    ? canChangeGoodsShelf(row)
    : row.goodsType !== 1 && row.auditStatus === 1;

  const handleSave = async (values: AuditValues): Promise<void> => {
    if (submitting.current || !allowed) return;
    submitting.current = true;
    setBusy(true);
    try {
      if (type === "shelf") await changeGoodsShelf({ id: row.id, isShelf });
      else await auditGoods(type === "approve"
        ? { id: row.id, auditStatus: 2 }
        : { id: row.id, auditStatus: 3, auditRemark: values.auditRemark?.trim() });
    } catch {
      // 请求层统一提示错误，保留当前输入。
      return;
    } finally {
      submitting.current = false;
      setBusy(false);
    }
    onSaved();
  };
  const handleCancel = (): void => {
    if (!submitting.current) onCancel();
  };
  const handleSubmit = (): void => form.submit();

  return <Modal
    open title={title} onCancel={handleCancel} onOk={handleSubmit}
    okText={type === "reject" ? "确认驳回" : type === "approve" ? "确认通过" : isShelf === 1 ? "确认上架" : "确认下架"}
    cancelText="取消" confirmLoading={busy}
    okButtonProps={{ disabled: !allowed, danger: type === "reject" }}
    cancelButtonProps={{ disabled: busy }} closable={!busy} keyboard={!busy} maskClosable={false}
  >
    <p>商品：{row.goodsName}</p>
    {type === "approve" && <p>审核通过后仍保持下架，请按需手动上架。</p>}
    {type === "shelf" && <p>{isShelf === 1 ? "确认将该商品上架？" : "确认将该商品下架？"}</p>}
    {!allowed && <Alert type="warning" message="当前商品状态不支持此操作，请刷新列表。" />}
    <Form form={form} layout="vertical" onFinish={handleSave} disabled={busy}>
      {type === "reject" && <Form.Item name="auditRemark" label="驳回原因" rules={[{ required: true, whitespace: true, message: "请输入驳回原因" }]}>
        <Input.TextArea rows={3} placeholder="请输入驳回原因" />
      </Form.Item>}
    </Form>
  </Modal>;
}
